// Role Form Component - Create/Edit Role with Policy Definition

import { ArrowLeft, Save } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { enterprisePermissionService } from '../../../services/enterprisePermissionService';
import { supabase } from '../../../utils/supabaseClient';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { RolePolicyEditor } from './RolePolicyEditor';
import type { Action, Resource, Role } from './types';

type RoleFormProps = {
  role?: Role | null;
  tenantId: string;
  onClose: () => void;
  onSave: () => void;
};

export const RoleForm: React.FC<RoleFormProps> = ({ role, tenantId, onClose, onSave }) => {
  const [roleName, setRoleName] = useState('');
  const [roleCode, setRoleCode] = useState('');
  const [description, setDescription] = useState('');
  const [resources, setResources] = useState<Resource[]>([]);
  const [actions, setActions] = useState<Action[]>([]);
  const [policyStatements, setPolicyStatements] = useState<any[]>([]);
  const [currentPolicyId, setCurrentPolicyId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (role) {
      setRoleName(role.roleName);
      setRoleCode(role.roleCode);
      setDescription(role.description || '');
      loadRolePolicies(role.roleId);
    } else {
      setRoleName('');
      setRoleCode('');
      setDescription('');
      setPolicyStatements({});
    }
    loadResourcesAndActions();
  }, [role, tenantId]);

  const loadResourcesAndActions = async () => {
    setIsLoading(true);
    try {
      const [resourcesData, actionsData] = await Promise.all([
        enterprisePermissionService.getResources(tenantId),
        enterprisePermissionService.getActions(tenantId),
      ]);
      setResources(resourcesData);
      setActions(actionsData);
    } catch (error) {
      console.error('Failed to load resources/actions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadRolePolicies = async (roleId: string) => {
    try {
      // Get policies linked to this role
      const { data: rolePolicies, error: rpError } = await supabase
        .from('base_role_policies')
        .select('policy_id')
        .eq('role_id', roleId)
        .eq('tenant_id', tenantId);

      if (rpError) throw rpError;

      if (rolePolicies && rolePolicies.length > 0) {
        const policyId = rolePolicies[0].policy_id;
        setCurrentPolicyId(policyId);
        
        // Load policy statements for the policy
        const statements = await enterprisePermissionService.getPolicyStatements(
          tenantId,
          policyId
        );
        
        // Transform statements to match RolePolicyEditor format
        const transformedStatements = statements.map((stmt: any) => ({
          resourceId: stmt.resourceId,
          actionId: stmt.policyStatement.actionId,
          effect: stmt.policyStatement.effect,
          priority: stmt.priority,
          rowCondition: stmt.policyStatement.rowCondition,
          columnCondition: stmt.policyStatement.columnCondition,
        }));
        
        setPolicyStatements(transformedStatements);
      }
    } catch (error) {
      console.error('Failed to load role policies:', error);
    }
  };

  const createPolicyStatements = async (policyId: string, statements: any[]) => {
    for (const stmt of statements) {
      const policyStatementJson = {
        actionId: stmt.actionId,
        effect: stmt.effect,
        rowCondition: stmt.rowCondition || {},
        columnCondition: stmt.columnCondition || {},
        timeCondition: {},
        contextCondition: {},
      };

      const { error } = await supabase.from('base_policy_statements').insert({
        tenant_id: tenantId,
        policy_id: policyId,
        resource_id: stmt.resourceId,
        policy_statement: policyStatementJson,
        priority: stmt.priority || 100,
        is_active: true,
      });

      if (error) {
        console.error('Failed to create policy statement:', error);
        throw error;
      }
    }
  };

  const handleSave = async () => {
    if (!roleName.trim()) {
      alert('Please enter a role name');
      return;
    }

    setIsSaving(true);
    try {
      const generatedRoleCode = roleCode || roleName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');

      // Create or update role
      let roleId: string;
      if (role) {
        // Update existing role
        const { data, error } = await supabase
          .from('base_roles')
          .update({
            role_name: roleName.trim(),
            role_code: generatedRoleCode,
            description: description.trim() || null,
            updated_at: new Date().toISOString(),
          })
          .eq('role_id', role.roleId)
          .eq('tenant_id', tenantId)
          .select('role_id')
          .single();

        if (error) throw error;
        roleId = data.role_id;
      } else {
        // Create new role
        const { data, error } = await supabase
          .from('base_roles')
          .insert({
            tenant_id: tenantId,
            role_name: roleName.trim(),
            role_code: generatedRoleCode,
            description: description.trim() || null,
            role_type: 'custom',
            is_active: true,
          })
          .select('role_id')
          .single();

        if (error) throw error;
        roleId = data.role_id;
      }

      // Create policy and link to role
      const policyId = await createRolePolicy(roleId);

      // Create policy statements if any
      if (policyStatements.length > 0) {
        await createPolicyStatements(policyId, policyStatements);
      }

      onSave();
    } catch (error) {
      console.error('Failed to save role:', error);
      alert('Failed to save role. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const createRolePolicy = async (roleId: string) => {
    // Create a policy for this role
    const policyName = `${roleName}_policy`;
    const policyCode = `${roleCode || roleName.toLowerCase().replace(/[^a-z0-9]+/g, '_')}_policy`;

    const { data: policy, error: policyError } = await supabase
      .from('base_policies')
      .insert({
        tenant_id: tenantId,
        policy_name: policyName,
        policy_code: policyCode,
        description: `Policy for role ${roleName}`,
        policy_type: 'custom',
        is_active: true,
      })
      .select('policy_id')
      .single();

    if (policyError) throw policyError;

    // Link policy to role
    const { error: linkError } = await supabase
      .from('base_role_policies')
      .insert({
        tenant_id: tenantId,
        role_id: roleId,
        policy_id: policy.policy_id,
      });

    if (linkError) throw linkError;

    return policy.policy_id;
  };

  return (
    <div className="flex flex-col h-full">
      <div className="border-b p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={onClose}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold">{role ? 'Edit Role' : 'Create Role'}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Define role details and permissions
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving || !roleName.trim()}>
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Role Details</CardTitle>
              <CardDescription>Basic information about the role</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="role-name">Role Name *</Label>
                <Input
                  id="role-name"
                  placeholder="Enter role name"
                  value={roleName}
                  onChange={(e) => {
                    setRoleName(e.target.value);
                    if (!roleCode && !role) {
                      setRoleCode(
                        e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, '_')
                          .replace(/^_+|_+$/g, '')
                      );
                    }
                  }}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="role-code">Role Code</Label>
                <Input
                  id="role-code"
                  placeholder="Auto-generated from role name"
                  value={roleCode}
                  onChange={(e) => setRoleCode(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Unique identifier for the role (auto-generated if not provided)
                </p>
              </div>
              <div>
                <Label htmlFor="role-description">Description</Label>
                <Textarea
                  id="role-description"
                  placeholder="Enter role description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="mt-1"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {!isLoading && resources.length > 0 && actions.length > 0 && (
            <RolePolicyEditor
              resources={resources}
              actions={actions}
              tenantId={tenantId}
              initialStatements={policyStatements}
              onStatementsChange={setPolicyStatements}
            />
          )}
        </div>
      </div>
    </div>
  );
};

